import type { UseQueryOptions } from '@/modules/query-client/types/UseQueryOptions';
import {
  QueryClient,
  // eslint-disable-next-line no-restricted-imports -- this is the file which overrides the @tanstack/react-query types
  useQuery as useTanstackQuery,
  type DefaultError,
  type QueryKey,
} from '@tanstack/react-query';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

/**
 * Wrapper around useQuery that allows for public and authenticated queries.
 * @param type - The type of query to run.
 * @param options - The options to pass to the query.
 * @param options.enabled - Whether the query should be enabled.
 * @param queryClient - The query client to use.
 * @returns The query result.
 */
export default function useQuery<
  TQueryFnData = unknown,
  TVariables = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  type: 'authenticated' | 'public',
  {
    enabled,
    ...options
  }: UseQueryOptions<TQueryFnData, TVariables, TError, TData, TQueryKey>,
  queryClient?: QueryClient
) {
  const userEnabled = useMemo(
    () => (isNil(enabled) ? true : enabled),
    [enabled]
  );

  return useTanstackQuery<TData, TError, TData, TQueryKey>(
    {
      ...options,
      enabled: userEnabled === true && (type === 'public' ? true : userEnabled),
    },
    queryClient
  );
}
